import { __awaiter } from "tslib";
import { callFandomService } from 'pathfinder/utils/api';
import units from 'pathfinder/modules/AffiliateUnitModule/units';
import deepmerge from 'deepmerge';
import { getAffiliateUnitCampaign, getAffiliateUnitCategory } from 'pathfinder/store/selectors';
import store from '../store/store';
import log from 'pathfinder/utils/log';
import { isLocalHost } from 'utils/env';
import { getGeo } from '@fandom/context';
export function getAvailableUnits(targeting, isForced = false) {
    var _a;
    // If working on localhost, then make sure to set the Geo cookie with a static country code value
    /* TODO: Maybe look into moving all the cookie extraction logic into a separate function, instead of polluting the code with all this logic. Might also help with re-usability down the line */
    let country;
    if (isLocalHost()) {
        country = 'US';
    }
    else {
        // Get the Geo cookie (gets set as a cookie whenever any wiki gets visited)
        country = (_a = getGeo()) === null || _a === void 0 ? void 0 : _a.country;
    }
    let potentialUnits = units.slice();
    // Filter by geo, and only display unit which have the matching countryCode (for example: 'US')
    potentialUnits = potentialUnits.filter((unit) => {
        const c = unit.country;
        return !Array.isArray(c) || c.length === 0 || c.indexOf(country) > -1;
    });
    // Filter by category and campaign, also add tracking to the list
    const availableUnits = [];
    potentialUnits.forEach((unit) => {
        // For every unit check every targeting, and add it to the list if there's a match
        targeting.forEach((target) => {
            if (unit.campaign === target.campaign && unit.category === target.category) {
                // Add tracking params coming from the service
                availableUnits.push(deepmerge(unit, {
                    tracking: target.tracking,
                    method: target.method,
                }));
            }
        });
    });
    return availableUnits;
}
function flattenAffiliateServiceResponse(response) {
    const targeting = [];
    response.forEach(function (campaign) {
        const campaignName = campaign.campaign;
        campaign.categories.forEach(function (category) {
            targeting.push({
                campaign: campaignName,
                category: category.name,
                score: category.score,
                tracking: category.tracking,
                recommendationLevel: category.recommendationLevel,
                method: category.method,
            });
        });
    });
    // sort by score
    targeting.sort(function (a, b) {
        return b.score - a.score;
    });
    // create page level and community level recommendations
    // NOTE items without `recommendationLevel` belong to both arrays
    const communityTargeting = [];
    const pageTargeting = [];
    targeting.forEach(function (t) {
        if (!t.recommendationLevel || t.recommendationLevel === 'page') {
            pageTargeting.push(t);
        }
        if (!t.recommendationLevel || t.recommendationLevel === 'community') {
            communityTargeting.push(t);
        }
    });
    return pageTargeting.length > 0 ? pageTargeting : communityTargeting;
}
const defaultedResponse = [
    {
        campaign: 'fanatical',
        category: 'fanatical',
        score: 1,
        tracking: [],
        recommendationLevel: 'defaulted',
        method: 'defaulted',
    },
];
function fetchTargetingFromAffiliateService({ cityId, articleId, }) {
    return __awaiter(this, void 0, void 0, function* () {
        // Both campaign and category query params MUST be set in order to allow for a proper AffiliateUnit to be served
        const debugCampaign = getAffiliateUnitCampaign(store.getState());
        const debugCategory = getAffiliateUnitCategory(store.getState());
        if (debugCampaign && debugCategory) {
            log.debug(`AffiliateUnit: Debugging params found for campaign: ${debugCampaign} and category: ${debugCategory}`);
            return [
                {
                    campaign: debugCampaign,
                    category: debugCategory,
                    score: 1,
                    tracking: [],
                    recommendationLevel: 'debug',
                    method: 'debug',
                },
            ];
        }
        let affiliateResponse;
        try {
            // affiliateResponse = await callFandomService('https://services.fandom.com/knowledge-graph/affiliates/18733/2025');
            affiliateResponse = yield callFandomService(`knowledge-graph/affiliates/${cityId}/${articleId}`);
        }
        catch (error) {
            log.debug('AffiliateUnit: Defaulting to fanatical. Failed to fetch affiliates data', error);
            return defaultedResponse;
        }
        // const affiliateResponse = await callFandomService('https://services.fandom.com/knowledge-graph/affiliates/147/452217');
        if (affiliateResponse.ok) {
            const resolvedAffiliateResponse = (yield affiliateResponse.json());
            if (!Array.isArray(resolvedAffiliateResponse) || resolvedAffiliateResponse.length === 0) {
                log.debug('AffiliateUnit: Defaulting to fanatical. No data returned for affiliates.');
                return defaultedResponse;
            }
            const flattenedAffiliateResponse = flattenAffiliateServiceResponse(resolvedAffiliateResponse);
            return flattenedAffiliateResponse;
        }
        log.debug('AffiliateUnit: Defaulting to fanatical. Response for affiliates not ok.');
        return defaultedResponse;
    });
}
export { fetchTargetingFromAffiliateService, flattenAffiliateServiceResponse };
